<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import ToolCard from 'dashboard/components-next/captain/assistant/ToolCard.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import CreateToolDialog from 'dashboard/components-next/captain/pageComponents/tool/CreateToolDialog.vue';
import AssistantSelector from 'dashboard/components-next/captain/pageComponents/AssistantSelector.vue';
import ToolPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/ToolPageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';

const store = useStore();

const uiFlags = useMapGetter('captainApiTools/getUIFlags');
const tools = useMapGetter('captainApiTools/getRecords');
const assistants = useMapGetter('captainAssistants/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const toolsMeta = useMapGetter('captainApiTools/getMeta');
const selectedAssistant = ref('all');

const selectedTool = ref(null);
const deleteToolDialog = ref(null);

const handleDelete = () => {
  deleteToolDialog.value.dialogRef.open();
};

const showCreateDialog = ref(false);
const createToolDialog = ref(null);

const shouldShowAssistantSelector = computed(() => {
  if (assistants.value.length === 0) return false;

  return !isFetching.value;
});

const handleCreateTool = () => {
  selectedTool.value = null;
  showCreateDialog.value = true;
  nextTick(() => createToolDialog.value.dialogRef.open());
};

const handleEditTool = () => {
  showCreateDialog.value = true;
  nextTick(() => createToolDialog.value.dialogRef.open());
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
  selectedTool.value = null;
};

const handleAction = ({ action, id }) => {
  selectedTool.value = tools.value.find(captainTool => id === captainTool.id);

  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    } else if (action === 'edit') {
      handleEditTool();
    }
  });
};

const fetchTools = (page = 1) => {
  const filterParams = { page };

  if (selectedAssistant.value !== 'all') {
    filterParams.assistantId = selectedAssistant.value;
  }
  store.dispatch('captainApiTools/get', filterParams);
};

const handleAssistantFilterChange = assistant => {
  selectedAssistant.value = assistant;
  fetchTools();
};

const onPageChange = page => fetchTools(page);

const onDeleteSuccess = () => {
  if (tools.value?.length === 0 && toolsMeta.value?.page > 1) {
    onPageChange(toolsMeta.value.page - 1);
  }
};

onMounted(() => {
  if (!assistants.value.length) {
    store.dispatch('captainAssistants/get');
  }
  fetchTools();
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.TOOLS.HEADER')"
    :button-label="$t('CAPTAIN.TOOLS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="toolsMeta.totalCount"
    :current-page="toolsMeta.page"
    :show-pagination-footer="!isFetching && !!tools.length"
    :is-fetching="isFetching"
    :is-empty="!tools.length"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @update:current-page="onPageChange"
    @click="handleCreateTool"
  >
    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('CAPTAIN.HEADER_KNOW_MORE')"
        :title="$t('CAPTAIN.TOOLS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('CAPTAIN.TOOLS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        fallback-thumbnail="/assets/images/dashboard/captain/tool-popover-light.svg"
        fallback-thumbnail-dark="/assets/images/dashboard/captain/tool-popover-dark.svg"
        learn-more-url="https://app.vowchat.ai/hc/"
      />
    </template>

    <template #emptyState>
      <ToolPageEmptyState @click="handleCreateTool" />
    </template>

    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #controls>
      <div v-if="shouldShowAssistantSelector" class="mb-4 -mt-3 flex gap-3">
        <AssistantSelector
          :assistant-id="selectedAssistant"
          @update="handleAssistantFilterChange"
        />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <ToolCard
          v-for="tool in tools"
          :id="tool.id"
          :key="tool.id"
          :name="tool.name"
          :title="tool.title"
          :description="tool.description"
          :assistant="tool.assistant"
          :api-spec="tool.api_spec"
          :enabled="tool.enabled"
          :created-at="tool.created_at"
          @action="handleAction"
        />
      </div>
    </template>

    <CreateToolDialog
      v-if="showCreateDialog"
      ref="createToolDialog"
      :tool="selectedTool"
      @close="handleCreateDialogClose"
    />
    <DeleteDialog
      v-if="selectedTool"
      ref="deleteToolDialog"
      :entity="selectedTool"
      type="ApiTools"
      @delete-success="onDeleteSuccess"
    />
  </PageLayout>
</template>
